import { dateToChinese } from "../../utils";
import { AREA_MODE, AREA_TICKET_TYPE } from "../../const/area";
import { HUB_ORDER_STATUS, HUB_ORDER_STATUS_MAP } from "../../const/hub-status";
import dayjs from "dayjs";

const DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const MONTH_DAY_TIME_FORMAT = "MM-DD HH:mm";
const MONTH_DAY_FORMAT = "MM-DD";
const TIME_FORMAT = "HH:mm";

/**
 * 
 *  枢纽站模式新增字段：
    curTime                      当前服务器时间:   yyyy-MM-dd HH:mm:ss
    arriveTime                   司机到达时间       yyyy-MM-dd HH:mm:ss
    driverPlanStartTime      司机预计出发时间 yyyy-MM-dd HH:mm:ss
    driverStartTime            司机出发时间       yyyy-MM-dd HH:mm:ss
    driverReceiveTimeLimit 司机最迟接单时间 yyyy-MM-dd HH:mm:ss
    receiveTime                 司机接单时间       yyyy-MM-dd HH:mm:ss
    driverWaitTimes           司机到站等待时间: 单位分钟
    driverWaitLeftTime        司机到站倒计时剩余时间：单位秒
    mode:                         0-原区域模式 1-枢纽模式-将要支持的枢纽模式
    ticketType:                  枢纽模式时: 0-实时单 1-预约单
    原数据:
    onTime:         司机出发后. 预计到达乘客上车的时间. 原格式 HH:mm
    offTime:         乘客上车后. 预计到达目的地的时间.    原格式  HH:mm
 */

/**
 * 比较时间
 * @param {*} time1 yyyy-MM-dd HH:mm:ss
 * @param {*} time2 yyyy-MM-dd HH:mm:ss
 */
function compareTime(time1, time2) {
  const result = {
    isBefore: false, // time1 < time2
    isBeforeOrSame: false, // time1 <= time2
    isAfter: false, // time1 > time2
    isAfterOrSame: false, // time1 >= time2
    isSame: false, // time1 = time2
  };
  if (!time1 || !time2) return result;

  try {
    const t1 = dayjs(time1, DATE_TIME_FORMAT);
    const t2 = dayjs(time2, DATE_TIME_FORMAT);
    result.isBefore = t1.isBefore(t2);
    result.isAfter = t1.isAfter(t2);
    result.isSame = t1.isSame(t2);
    result.isBeforeOrSame = result.isBefore || result.isSame;
    result.isAfterOrSame = result.isAfter || result.isSame;
  } catch (error) {
    console.error("compareTime fail:", error);
  }

  return result;
}

export function formatDateTime(dt, hiddenDate = false) {
  if (!dt) return "--";
  // 若是当天，不显示日期
  if(dayjs().isSame(dt, "day") && hiddenDate) return dayjs(dt, DATE_TIME_FORMAT).format(TIME_FORMAT);
  return dayjs(dt, DATE_TIME_FORMAT).format(MONTH_DAY_TIME_FORMAT);
}

function formatTime(dt) {
  if (!dt) return "";
  return dayjs(dt, DATE_TIME_FORMAT).format(TIME_FORMAT);
}

function formatMD(dt, hiddenDate = false) {
  if (!dt) return "";
  // 若是当天，不显示日期
  if (dayjs().isSame(dt, "day") && hiddenDate) return "";
  return dayjs(dt, DATE_TIME_FORMAT).format(MONTH_DAY_FORMAT);
}

function checkIsStatusChange(orderInfo, oldOrderInfo) {
  const { id, status } = orderInfo || {};
  const { id: oldId, status: oldStatus } = oldOrderInfo || {};
  const isStatusChange = id === oldId && status !== oldStatus && oldStatus;
  return isStatusChange;
}

/**
 * 预约单
 */
function convertHubBookingOrder(orderInfo, oldOrderInfo) {
  const {
    status,
    curTime,
    arriveTime,
    driverPlanStartTime,
    driverStartTime,
    driverReceiveTimeLimit,
    receiveTime,
    onTime,
    offTime,
    expireTime,
    onStopName,
    driverWaitLeftTime,
  } = orderInfo || {};

  const isStatusChange = checkIsStatusChange(orderInfo, oldOrderInfo);

  let hubStatusText = HUB_ORDER_STATUS_MAP[status] || ""; // 状态对应描述
  let hubStatusDesc = "";
  let hubDriverWaitLeftTimeDesc = "";
  let hubCancelModalDesc = "";

  const driverReceiveTimeLimitCompare = compareTime(
    curTime,
    driverReceiveTimeLimit
  ); // 当前时间与driverReceiveTimeLimitCompare比较
  const expireTimeCompare = compareTime(curTime, expireTime); // 当前时间与expireTime比较
  const driverPlanStartTimeCompare = compareTime(curTime, driverPlanStartTime); // 当前时间与driverPlanStartTime比较

  // 待接单
  if (status === HUB_ORDER_STATUS.HUB_PAYED || status === HUB_ORDER_STATUS.HUB_PAYED_NEW) {
    if (driverReceiveTimeLimitCompare.isBefore) {
      const md = formatMD(driverReceiveTimeLimit, true);
      const time = formatTime(driverReceiveTimeLimit);
      hubStatusText = `${HUB_ORDER_STATUS_MAP[status] || ""}${ time ? `<span class="subtitle">预计${md}<span class="blue">${time}</span>前接单</span>`: ''}`;
      hubStatusDesc = `<span class="blue">订单已确认，</span>正在等待司机接单`;
    }

    if (
      driverReceiveTimeLimitCompare.isAfterOrSame &&
      expireTimeCompare.isBeforeOrSame
    ) {
      const time = formatDateTime(expireTime, true);
      hubStatusDesc = `正在等待司机接单，<span class="blue">${time}</span>前如无司机接单，我们将为您自动取消订单`;
    }

    if (receiveTime) {
      const md = formatMD(driverPlanStartTime, true);
      const time = formatTime(driverPlanStartTime);
      if (isStatusChange) {
        hubStatusText = `${HUB_ORDER_STATUS_MAP[status] || ""}${ time ? `<span class="subtitle">预计${md}<span class="blue">${time}</span>出发</span>`: ''}`;
        hubStatusDesc = `<span class="blue">司机已接单，</span>正在等待司机出发`;
        hubCancelModalDesc = isStatusChange
          ? `很遗憾的通知您司机已经取消订单，我们正在为您寻找其他司机，请耐心等待`
          : "";
      }
    }
  }

  // 已取消（这种情况应该不存在）
  if (status === HUB_ORDER_STATUS.HUB_HAS_CANCEL) {
    if (expireTimeCompare.isAfterOrSame) {
      hubStatusDesc = `<span class="subtitle">很遗憾的通知您当前无空闲车辆，您的的订单已取消</span>`;
    }
    if (receiveTime) {
      const time = formatDateTime(receiveTime, true);
      hubStatusDesc = `<span class="subtitle">司机已接单，</span>预计在<span class="blue">${time}</span>出发`;
    }
  }

  // 待出发
  if (status === HUB_ORDER_STATUS.HUB_HAS_ACCEPT) {
    if (driverPlanStartTimeCompare.isBeforeOrSame) {
      const md = formatMD(driverPlanStartTime, true);
      const time = formatTime(driverPlanStartTime);
      hubStatusText = `${HUB_ORDER_STATUS_MAP[status] || ""}${ time ? `<span class="subtitle">预计${md}<span class="blue">${time}</span>出发</span>`: ''}`;
      hubStatusDesc = `<span class="blue">司机已接单，</span>正在等待司机出发`;
    }
    if (driverPlanStartTimeCompare.isAfter) {
      hubStatusDesc = `<span class="blue">司机已接单，</span>正在等待司机出发`;
    }
    if (driverStartTime && !arriveTime) {
      hubStatusText = `未上车 ${ onTime ? `<span class="subtitle">预计<span class="blue">${onTime}</span>到达上车点</span>`: ''}`;
      const name = onStopName || "上车站";
      hubStatusDesc = `建议提前到达<span class="blue">${name}</span>等候，以免错过乘车`;
    }
    if (driverStartTime && arriveTime) {
      if (driverWaitLeftTime === 0) {
        hubStatusText = `车辆已到站<span class="subtitle">车辆已超时等候</span>`;
        hubStatusDesc = "订单将可能被司机无责结束";
      } else {
        hubStatusText = "车辆已到站";
        hubStatusDesc = `<span class="blue">倒计时结束</span>司机可无责取消订单`;
      }
      hubDriverWaitLeftTimeDesc = driverWaitLeftTime;
    }
  }

  // 已上车
  if (status === HUB_ORDER_STATUS.HUB_PASSENGER_GET_ON) {
    hubDriverWaitLeftTimeDesc = '';
    hubStatusText = `已上车 <span class="subtitle">预计<span class="blue">${offTime || '--'}</span>到达下车点</span>`;
  }

  return {
    isStatusChange: checkIsStatusChange(orderInfo, oldOrderInfo),
    isDispatching: status === HUB_ORDER_STATUS.HUB_PAYED || status === HUB_ORDER_STATUS.HUB_PAYED_NEW,
    isWaiting: status === HUB_ORDER_STATUS.HUB_HAS_ACCEPT,
    isPendding: status === HUB_ORDER_STATUS.HUB_PASSENGER_GET_ON,
    isCancel: status === HUB_ORDER_STATUS.HUB_HAS_CANCEL,
    hubStatusText,
    hubStatusDesc,
    hubDriverWaitLeftTimeDesc,
    hubCancelModalDesc,
  };
}

/**
 * 实时单
 */
function convertHubRealtimeOrder(orderInfo, oldOrderInfo) {
  const {
    status,
    curTime,
    arriveTime,
    driverStartTime,
    driverReceiveTimeLimit,
    receiveTime,
    onTime,
    offTime,
    expireTime,
    onStopName,
    driverWaitLeftTime,
  } = orderInfo || {};
  const isStatusChange = checkIsStatusChange(orderInfo, oldOrderInfo);

  let hubStatusText = HUB_ORDER_STATUS_MAP[status] || ""; // 状态对应描述
  let hubStatusDesc = ""; // 显示文案描述
  let hubDriverWaitLeftTimeDesc = ""; // 司机等待时间倒计时
  let hubCancelModalDesc = ""; // 取消弹框描述

  const driverReceiveTimeLimitCompare = compareTime(
    curTime,
    driverReceiveTimeLimit
  ); // 当前时间与driverReceiveTimeLimitCompare比较
  const expireTimeCompare = compareTime(curTime, expireTime); // 当前时间与expireTime比较

  // 待接单
  if (status === HUB_ORDER_STATUS.HUB_PAYED) {
    if (driverReceiveTimeLimitCompare.isBeforeOrSame) {
      const time = formatTime(driverReceiveTimeLimit);
      hubStatusDesc = `正在等待司机接单，<span class="blue">${time}</span>前如无司机接单，我们将为您自动取消订单`;
    }
    if (receiveTime) {
      hubCancelModalDesc = isStatusChange
        ? `很遗憾的通知您司机已经取消订单，我们正在为您寻找其他司机，请耐心等待`
        : "";
    }
  }

  // 已取消
  if (status === HUB_ORDER_STATUS.HUB_HAS_CANCEL) {
    if (expireTimeCompare.isAfterOrSame) {
      hubStatusDesc = `<span class="subtitle">很遗憾的通知您当前无空闲车辆，您的的订单已取消</span>`;
    }
    if (receiveTime) {
      hubStatusDesc = `<span class="subtitle">司机已接单，正在等待司机出发</span>`;
    }
  }

  // 待出发
  if (status === HUB_ORDER_STATUS.HUB_HAS_ACCEPT) {
    hubStatusDesc = `<span class="blue">司机已接单，</span>正在等待司机出发`;

    if (driverStartTime && !arriveTime) {
      hubStatusText = `未上车 ${ onTime ? `<span class="subtitle">预计<span class="blue">${onTime}</span>到达上车点</span>`: ''}`;
      const name = onStopName || "上车站";
      hubStatusDesc = `建议提前到达<span class="blue">${name}</span>等候，以免错过乘车`;


    }
    if (driverStartTime && arriveTime) {
      if (driverWaitLeftTime === 0) {
        hubStatusText = `车辆已到站<span class="subtitle">车辆已超时等候</span>`;
        hubStatusDesc = "订单将可能被司机无责结束";
      } else {
        hubStatusText = "司机已到达";
        hubStatusDesc = `请尽快前往上车点，<span class="blue">超时后</span>司机可无责取消订单`;
      }
      hubDriverWaitLeftTimeDesc = driverWaitLeftTime;
    }
  }

  // 已上车
  if (status === HUB_ORDER_STATUS.HUB_PASSENGER_GET_ON) {
    hubDriverWaitLeftTimeDesc = '';
    hubStatusText = `已上车 <span class="subtitle">预计<span class="blue">${offTime || '--'}</span>下车点</span>`;
  }

  return {
    isDispatching: status === HUB_ORDER_STATUS.HUB_PAYED,
    isWaiting: status === HUB_ORDER_STATUS.HUB_HAS_ACCEPT,
    isPendding: status === HUB_ORDER_STATUS.HUB_PASSENGER_GET_ON,
    isCancel: status === HUB_ORDER_STATUS.HUB_HAS_CANCEL,
    hubStatusText,
    hubStatusDesc,
    hubDriverWaitLeftTimeDesc,
    hubCancelModalDesc,
  };
}

export function convertHubOrder(orderInfo, oldOrderInfo = {}) {
  if (!orderInfo) return null;
  const { mode, ticketType, useBookingDate, useBookingTimeRange } = orderInfo;

  const isHubOrder = mode === AREA_MODE.HUB; // 是否枢纽站模式订单
  const isHubBookingOrder = ticketType === AREA_TICKET_TYPE.BOOKING_ORDER; // 是否预约单
  const hubBookingOrderDesc = isHubBookingOrder ? "预约单" : "实时单";
  const hubOrderTimeDesc = isHubBookingOrder
    ? `${formatMD(useBookingDate)} ${useBookingTimeRange}`
    : "随时出发";
  const hubOrderDateTimeDesc = isHubBookingOrder
    ? `${dateToChinese(useBookingDate)}${useBookingTimeRange}`
    : "";

  const data = isHubBookingOrder
    ? convertHubBookingOrder(orderInfo, oldOrderInfo)
    : convertHubRealtimeOrder(orderInfo, oldOrderInfo);

  return {
    isHubOrder,
    isHubBookingOrder,
    hubBookingOrderDesc,
    hubOrderTimeDesc,
    hubOrderDateTimeDesc,
    ...data,
  };
}
